<template>
  <div class="feed-wrapper">
    <iBiDirectionalInfiniteScroll
      ref="feedScroller"
      class="feed-scroll"
      :class="{reversed}"
      :loading-top="getLoading('top')"
      :has-more-top="getHasMore('top')"
      :loading-bottom="getLoading('bottom')"
      :has-more-bottom="getHasMore('bottom')"
      enable-bottom-load
      enable-top-load
      :threshold="1000"
      @load:top="handleLoadTop"
      @load:bottom="handleLoadBottom"
      @scroll="handleOnScroll"
    >
      <!--TODO: create element to select component layout-->
      <template v-if="!editMode">
        <component
          :is="layout"
          v-for="entity in entities"
          :key="`${entity.entityType}-${entity.pkString}`"
          :contexts="getItemContexts(entity)"
        />
      </template>
      <SmartFeedPlaceholder
        v-else
        :smart-feed-layout="getOptionValue(SmartFeedLayout)"
        entity-layout="cardLarge"
      />

      <slot v-if="entities.length === 0" name="empty">
        <iRow
          width="fill"
          height="hug"
          align="center"
          vertical-align="middle"
          :style-overrides="{
            ...resolveSpacingOverrides('standard', 'padding'),
          }"
        >
          <iText>{{ getOptionValue(EmptyStateText) }}</iText>
        </iRow>
      </slot>
    </iBiDirectionalInfiniteScroll>
  </div>
</template>

<script>
import { DynamicElementMixin } from "%/dynamic-components/render-engine/mixins/production/dynamic-element-mixin";
import DynamicElementWithContext from "%/dynamic-components/render-engine/mixins/production/dynamic-element-with-context-mixin";
import App from "+/Engine/App";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import LayoutGap from "%/dynamic-components/render-engine/options/layout-options/layout-gap";
import MaxWidth from "%/dynamic-components/render-engine/options/spacing-options/max-width";
import MinWidth from "%/dynamic-components/render-engine/options/spacing-options/min-width";
import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import AppFeedOptions from "~/dynamic-components/bundles/app-feed-options";
import AppFeedComponent from "~/dynamic-components/options/app-feed-component";
import SmartFeedPlaceholder from "SmartFeed/components/SmartFeedPlaceholder.vue";
import SmartFeedLayout from "~/dynamic-components/options/smart-feed-layout";
import EmptyStateText from "%/dynamic-components/render-engine/options/empty-state-text";

export default {
  name: "DynamicAppFeedElement",
  components: { SmartFeedPlaceholder },
  mixins: [DynamicElementMixin, DynamicElementWithContext],
  elementPanelSettings: {
    displayName: "Feed",
    elementGroup: "general",
    displayIcon: "feed",
    elementType: "element",
  },
  optionBundles: [
    new AppearanceOptions({}, {}, false),
    AppFeedOptions,
  ],
  options: [
    LayoutGap,
    MaxWidth,
    MinWidth,
    new LayoutWidth({
      "default": "fill",
    }),
  ],
  hidden: true,
  computed: {
    SmartFeedLayout() {
      return SmartFeedLayout;
    },
    EmptyStateText() {
      return EmptyStateText;
    },
    // The picked row component + the context name its row entity binds to.
    // Without these the published feed resolves neither.
    appFeedComponentValue() {
      return this.getOptionValue(AppFeedComponent) ?? {};
    },
    layoutComponentName() {
      return this.appFeedComponentValue.componentName ?? this.appFeedComponentValue.componentId ?? null;
    },
    contextVarName() {
      return this.appFeedComponentValue.contextVarName ?? "item";
    },
    layout() {
      if (this.editMode) {
        return;
      }
      const app = App.get(this.appName);
      return app.getComponent(this.layoutComponentName);
    },
    entities() {
      if (this.editMode) {
        return [1, 2, 3, 4, 5, 6];
      }
      const app = App.get(this.appName);
      const context = this.contexts[this.entityContext];
      const itemsWithoutPkString = app.getFeedItems(context.entityType, context.pk, this.fieldName);
      return itemsWithoutPkString.map(item => {
        item.pkString = App.getPkString(item.entityPk);
        return item;
      });
    },
  },
  mounted() {
    this.restoreLastKnownScrollPositionY();
  },
  methods: {
    handleOnScroll(scrollTop) {
      if (this.editMode) {
        return;
      }
      const app = App.get(this.appName);
      const context = this.contexts[this.entityContext];
      app.setLastKnownScrollPositionY(context.entityType, context.pk, this.fieldName, scrollTop);
    },
    restoreLastKnownScrollPositionY() {
      if (this.editMode) {
        return;
      }
      const app = App.get(this.appName);
      const context = this.contexts[this.entityContext];
      const lastKnownScrollPositionY = app.getLastKnownScrollPositionY(context.entityType, context.pk, this.fieldName);
      if (lastKnownScrollPositionY) {
        this.$nextTick(() => {
          this.$refs["feedScroller"].scrollToXAndY(0, lastKnownScrollPositionY);
        });
      }
    },
    handleLoadTop() {
      if (this.editMode) {
        return;
      }
      if (this.reversed) {
        this.loadMoreNext();
      } else {
        this.loadMorePrevious();
      }
    },
    handleLoadBottom() {
      if (this.editMode) {
        return;
      }
      if (this.reversed) {
        this.loadMorePrevious();
      } else {
        this.loadMoreNext();
      }
    },
    loadMoreNext() {
      const app = App.get(this.appName);
      const context = this.contexts[this.entityContext];
      app.loadMoreNext(context.entityType, context.pk, this.fieldName);
    },
    loadMorePrevious() {
      const app = App.get(this.appName);
      const context = this.contexts[this.entityContext];
      app.loadMorePrevious(context.entityType, context.pk, this.fieldName);
    },

    getItemContexts(item) {
      if (this.editMode) {
        return;
      }
      const rowAnchor = {
        [this.contextVarName]: {
          entityType: item.entityType,
          pk: item.entityPk,
        },
      };
      return {
        ...this.contexts,
        ...rowAnchor,
        ...this.resolveBoundExtraContexts(item, rowAnchor),
      };
    },
    // Extra context bindings per row: a walk off THIS row, or a page context.
    // Warn rather than throw — the feed re-renders as rows hydrate.
    resolveBoundExtraContexts(item, rowAnchor) {
      const bindings = this.appFeedComponentValue?.contexts ?? {};
      const resolved = {};
      Object.entries(bindings).forEach(([contextName, entry]) => {
        if (!entry || contextName === this.contextVarName) {
          return;
        }
        if (entry.entitySource === "context" && entry.sourceContext === this.contextVarName) {
          if (!entry.sourceField) {
            resolved[contextName] = rowAnchor[this.contextVarName];
            return;
          }
          const target = App.get(this.appName)
              .getEntityField(item.entityType, item.entityPk, entry.sourceField);
          const targetPk = target?.pk ?? target?.entityPk ?? null;
          if (target && targetPk) {
            resolved[contextName] = {
              entityType: target.entityType ?? entry.entityType,
              pk: targetPk,
            };
            return;
          }
          // eslint-disable-next-line no-console
          console.warn(
            `DynamicAppFeedElement: context "${contextName}" unresolved — "${entry.sourceField}" `
            + "off the feed item is empty or not yet hydrated; the row renders without it.",
            { contextName, sourceField: entry.sourceField, entityType: item.entityType, entityPk: item.entityPk },
          );
          return;
        }
        const pageCtx = entry.sourceContext ? this.contexts?.[entry.sourceContext] : null;
        if (pageCtx) {
          resolved[contextName] = pageCtx;
          return;
        }
        if (entry.sourceContext) {
          // eslint-disable-next-line no-console
          console.warn(
            `DynamicAppFeedElement: context "${contextName}" is bound to page context `
            + `"${entry.sourceContext}", which this page does not provide.`,
            { contextName, sourceContext: entry.sourceContext, available: Object.keys(this.contexts ?? {}) },
          );
        }
      });
      return resolved;
    },
    getLoading(direction) {
      if (this.editMode) {
        return false;
      }
      const app = App.get(this.appName);
      const context = this.contexts[this.entityContext];

      if (this.reversed) {
        if (direction === "top") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "isLoadingNext");
        }
        if (direction === "bottom") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "isLoadingPrevious");
        }
      } else {
        if (direction === "top") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "isLoadingPrevious");
        }
        if (direction === "bottom") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "isLoadingNext");
        }
      }
      return false;
    },
    getHasMore(direction) {
      if (this.editMode) {
        return;
      }
      const app = App.get(this.appName);
      const context = this.contexts[this.entityContext];

      if (this.reversed) {
        if (direction === "top") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "hasMoreNext");
        }
        if (direction === "bottom") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "hasMorePrevious");
        }
      } else {
        if (direction === "top") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "hasMorePrevious");
        }
        if (direction === "bottom") {
          return app.getFeedMeta(context.entityType, context.pk, this.fieldName, "hasMoreNext");
        }
      }
      return false;
    },
  },
};
</script>

<style scoped lang="scss">
.feed-wrapper {
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.feed-scroll {
  flex: 1;
  overflow-y: auto;
}

.feed-scroll.reversed {
  display: flex;
  flex-direction: column-reverse;
}
</style>
